import { Button, HStack, Input, Text, useClipboard, VStack } from '@chakra-ui/react';
import { gateForErrorCode } from '@bower/shared/billing/gates';
import React from 'react';
import { MaterialIcon } from '../../ui/MaterialIcon';
import { useAppToast } from '../../../hooks/ui/useAppToast';
import {
  useShareLinks,
  useCreateShareLink,
  useRevokeShareLink,
} from '../../../hooks/data/useShareLinks';
import { BowerSpinner } from '../../ui/BowerSpinner';
import { ApiResponseError } from '../../../services/api/http-client.types';

interface ShareToWebProps {
  artifactId: string;
  isRestricted: boolean;
}

/**
 * Public share-link controls.  Composed into the same dialog as the
 * entity-ACL controls (Notion's pattern: "Share to web" + "Share with
 * people" in one place).  Public links bypass entity ACL by design —
 * anyone with the link reads regardless of RESTRICTED status — so when
 * both are active we surface a banner so the user understands the
 * interaction.
 *
 * Only renders for artifacts; project sharing happens through the
 * workspace-membership flow.  Caller gates on `entityType === 'ARTIFACT'`.
 */
export function ShareToWeb({ artifactId, isRestricted }: ShareToWebProps): React.ReactElement {
  const toast = useAppToast();
  const { data: links, isLoading } = useShareLinks(artifactId);
  const createLink = useCreateShareLink();
  const revokeLink = useRevokeShareLink();

  const activeLink = links?.[0] ?? null;
  const { hasCopied, onCopy } = useClipboard(activeLink?.shareUrl ?? '');

  const handleCreate = async (): Promise<void> => {
    try {
      await createLink.mutateAsync({ artifactId });
    } catch (error) {
      // A tier refusal: the API client's 402 interceptor has already opened
      // the upgrade prompt, which says why. A failure toast on top would
      // contradict it. Same condition the interceptor opens the prompt on, so
      // a 402 with an unmapped code still gets the toast instead of nothing.
      if (
        error instanceof ApiResponseError &&
        error.status === 402 &&
        error.code !== undefined &&
        gateForErrorCode(error.code) !== null
      ) {
        return;
      }
      toast({ status: 'error', title: 'Could not generate share link' });
    }
  };

  const handleRevoke = async (): Promise<void> => {
    if (!activeLink) return;
    try {
      await revokeLink.mutateAsync({ artifactId, shareLinkId: activeLink.id });
    } catch {
      toast({ status: 'error', title: 'Could not revoke share link' });
    }
  };

  return (
    <VStack align="stretch" spacing={2}>
      {/* Header: title only.  When no link exists, the inline "Get
          link" button on the right doubles as the action; with a
          link present, the destructive action moves down next to
          Copy where the user is already focused (Notion / Figma
          pattern — destructive action lives near the affordance it
          undoes, never in the header). */}
      <HStack justify="space-between">
        <HStack spacing={2}>
          <MaterialIcon name="public" boxSize="16px" color="text.secondary" />
          <Text fontSize="sm" fontWeight="semibold" color="text.primary">
            Share to web
          </Text>
        </HStack>
        {isLoading && <BowerSpinner size="xs" />}
        {!isLoading && !activeLink && (
          <Button
            size="xs"
            variant="link"
            color="text.link"
            isLoading={createLink.isPending}
            onClick={handleCreate}
          >
            Get link
          </Button>
        )}
      </HStack>

      {activeLink && (
        <>
          <HStack spacing={2} align="flex-start">
            {/* Left column: URL input on top, view-count caption below.
                The caption sits on the same row as the Disable button
                in the right column, which keeps the dialog compact —
                "N views" gets a free line of vertical real estate
                instead of pushing everything down. */}
            <VStack spacing={1} align="stretch" flex={1}>
              <Input
                value={activeLink.shareUrl ?? ''}
                isReadOnly
                size="sm"
                fontSize="xs"
                fontFamily="mono"
                bg="bg.surface"
              />
              <Text fontSize="xs" color="text.subtle" px={1}>
                {activeLink.viewCount} {activeLink.viewCount === 1 ? 'view' : 'views'}
              </Text>
            </VStack>
            <VStack spacing={1} align="stretch">
              <Button
                size="sm"
                variant="outline"
                leftIcon={
                  <MaterialIcon name={hasCopied ? 'check' : 'content_copy'} boxSize="14px" />
                }
                onClick={onCopy}
              >
                {hasCopied ? 'Copied' : 'Copy'}
              </Button>
              <Button
                size="sm"
                variant="outline-danger"
                leftIcon={<MaterialIcon name="delete" boxSize="14px" />}
                isLoading={revokeLink.isPending}
                onClick={handleRevoke}
              >
                Disable
              </Button>
            </VStack>
          </HStack>

          {isRestricted && (
            <HStack
              spacing={2}
              px={3}
              py={2}
              bg="bg.warning"
              borderRadius="md"
              borderWidth="1px"
              borderColor="border.warning"
            >
              <MaterialIcon name="warning" boxSize="14px" color="text.warning" />
              <Text fontSize="xs" color="text.primary">
                Anyone with this URL can view the artifact, even people not on the access list.
              </Text>
            </HStack>
          )}
        </>
      )}
    </VStack>
  );
}
